import type { KpiTrendItem } from '@/lib/api';
import type { CsPeriod, Period } from '@/components/ui/PeriodToggle';
import type { TrendPoint } from '@/components/charts/TrendChart';
import { kpiDef, type KpiId } from '@/lib/kpi-definitions';

/** 月次系列の各指標。集約方法は KPI 定義レジストリ側で持つ */
export type MonthlyMetric = KpiId;

/**
 * 四半期・年度集約の方法は KPI 定義レジストリ（kpi-definitions.ts）の aggregation を参照する。
 * ラベルと実際の集約が同じ定義を読むため、片方だけ直して食い違う事故が起きない。
 *
 * ⚠️ 率の単純平均は、月ごとの母数差を無視した近似。厳密には母数で加重すべきだが、
 * ダッシュボードでは月次の母数を配信していないため単純平均としている。
 * 画面ではY軸ラベルに「四半期平均」「年度平均」と明示して誤読を防ぐ。
 */
/** @graph-connects none */
function isSumMetric(metric: MonthlyMetric): boolean {
  return kpiDef(metric).aggregation === 'sum';
}

/** 'YYYY-MM-01' → 'YYYY Qn' */
/** @graph-connects none */
function toQuarterLabel(date: string): string {
  const [year, month] = date.split('-');
  const quarter = Math.floor((Number(month) - 1) / 3) + 1;
  return `${year} Q${String(quarter)}`;
}

/**
 * 'YYYY-MM-01' → 'FYyyyy'
 * airCloset は6月期決算なので、年度は7月始まり。2026-07〜2027-06 が FY2027。
 * 予算シート『2027年6月期目標予実管理』と同じ期区切りに揃えてある。
 */
/** @graph-connects none */
function toFiscalYearLabel(date: string): string {
  const [year, month] = date.split('-');
  const fiscalYear = Number(month) >= 7 ? Number(year) + 1 : Number(year);
  return `FY${String(fiscalYear)}`;
}

/** 'YYYY-MM-01' → 'YYYY-MM' */
/** @graph-connects none */
function toMonthLabel(date: string): string {
  return date.slice(0, 7);
}

/**
 * 'YYYY-MM-DD'（週の開始日）→ 'MM/DD'。
 * API が week 粒度で返す点はどれも月曜起点の開始日なので、
 * toWeekLabel で丸め直さずそのままラベルにする。
 * ※ 週が何を指すか（登録週 / 発生週）は periodAxisLabel 側で出し分ける。
 */
/** @graph-connects none */
function toWeekStartLabel(date: string): string {
  const [, month, day] = date.split('-');
  return `${month}/${day}`;
}

/**
 * 'YYYY-MM-DD' → その日を含む週の月曜日 'MM/DD'。
 * BigQuery 側の週次ビュー（DATE_TRUNC(..., WEEK(MONDAY))）と同じ区切りに揃えている。
 */
/** @graph-connects none */
function toWeekLabel(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  // getUTCDay() は日曜=0。月曜起点にするため日曜だけ6日戻す
  const offset = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - offset);
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${month}/${day}`;
}

/** @graph-connects none */
function bucketLabel(period: Period, date: string): string {
  if (period === 'month') return toMonthLabel(date);
  if (period === 'quarter') return toQuarterLabel(date);
  return toFiscalYearLabel(date);
}

/**
 * 週次の点を **API から直接もらう**指標。日次からは積み上げない。
 *
 * - 成功率2本: 登録週コホートがネイティブ粒度（オンボ94日ラグ / ロイヤル187日ラグ）
 * - 退会者数: ac_records の日次行を API 側で週合計済み（完了週のみ）
 * - 待ち時間の SLO 2本: `sla_responses` を API 側で週集計済み（問い合わせが発生した週・完了週のみ）
 * - 解決率系2本: `chat_resolutions` を API 側で週集計済み（同上）
 *
 * これ以外の指標は日次の点から週へ畳むが、**日次の点を返すのは development のモックだけ**。
 * つまりこの集合に入れ忘れた指標は、本番で週次グラフが黙って空になる。
 * API に週次を足したら**必ずここにも足すこと**。
 *
 * ⚠️ **同じ入れ忘れが2回起きている**（2026-10-08 に初動SLO・回答SLO / 2026-10-28 に
 * 3ターム解決率・解決率）。どちらも月次だけが出て週次が空になり、画面上は「未配線」と
 * 見分けが付かなかった。**この集合は API 側の `WeekPoint`（routes.ts）と1対1で対応する**
 * ——あちらに週の点を足したらここにも足す、が守るべき不変条件。
 *
 * @graph-connects none
 */
const WEEK_NATIVE_METRICS = new Set<MonthlyMetric>([
  'onboardingRate',
  'loyaltyRate',
  'churnCount',
  'firstResponseSlaRate',
  'answerSlaRate',
  'threeTurnRate',
  'resolutionRate',
]);

/**
 * 週の意味が「**いつ登録した人か**」である指標。軸ラベルと集約ラベルの出し分けに使う。
 *
 * 同じ月曜起点の週でも、退会者数の週は「いつ退会が起きたか」で意味が違う。
 * 区切りが同じだからといって同じラベルを出すと、読み手がコホートと出来事を取り違える。
 *
 * @graph-connects none
 */
const COHORT_WEEK_METRICS = new Set<MonthlyMetric>(['onboardingRate', 'loyaltyRate']);

/**
 * 値のある点。`drawnPoints` を通した系列は null を含まないことを**型で**言い切る。
 * 読む側が「ここは null かもしれない」を各自で判断し直す余地を残さないための型。
 */
/** @graph-connects none */
export interface DrawnPoint extends TrendPoint {
  value: number;
}

/**
 * 実際に線として描く点だけを残す。**TrendChart が描く点も、カードの数字（latestValue）も
 * これを通す。**
 *
 * ⚠️ **月次だけ、値が null の点が末尾に並ぶ。** 月次系列の spine は退会者数（当月まである）で、
 * ラグのある指標（オンボ94日 / ロイヤル187日）はそこに `?? null` で埋められる。週次・四半期・
 * 年度は値の無い点を落としているので、月次だけ「点の末尾」と「線の末尾」がずれ、カードが
 * — になっていた（2026-10-29 修正）。**どの点を描くかの判定はここ1箇所に置く。**
 *
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business トレンド系列のうち値のある点だけを返す。チャートの線とカードの数字を同じ点から取るために使う
 * @graph-connects none
 */
export function drawnPoints(data: TrendPoint[]): DrawnPoint[] {
  return data.filter((d): d is DrawnPoint => d.value != null);
}

/**
 * 系列の最新値。**カードに出す数字はこれを使う。**
 *
 * カードが `/kpi` スナップショット（＝常に直近の確定「月」）を直接読むと、期間トグルを
 * 切り替えても数字が動かず、グラフだけが変わる。さらに KGI は増減だけ系列から取っていたため、
 * 四半期を選ぶと「単月の率」に「前四半期比」が付くという食い違いが起きていた。
 * **描いている系列の末尾を出す**ことで、カードとグラフが構造的にずれなくなる。
 *
 * ⚠️ **「末尾の点」ではなく「描いている線の末尾」を返す**（`drawnPoints`）。月次系列は
 * 退会者数の月を spine にしていて、ラグのある指標は末尾が null の点で埋まる。素の末尾を
 * 読むとグラフに線が出ているのにカードだけ — になる（2026-10-29 修正）。
 * 裏返しとして、取り込みが止まって末尾が欠けても — にはならず**最後に値のあった期間を出す**
 * ので、鮮度は画面上部の対象日で見る。
 *
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 集約済みトレンドのうち値のある最新期間の値を返す。カードの数字をグラフと一致させるために使う
 * @graph-connects drawnPoints [calls] 描画対象の点に絞る
 */
export function latestValue(points: TrendPoint[]): number | null {
  const drawn = drawnPoints(points);
  // Array#at は web 側の tsconfig の lib 対象外（es2021）なので添字で取る
  return drawn.length >= 1 ? drawn[drawn.length - 1].value : null;
}

/**
 * 系列の最新期間のラベル（'2026-09' / '2026 Q3' / 'FY2027' / '09/29'）。
 * カードの見出しに出す期間を、値と同じ点から取るために使う。
 * 値と同じ点を指すため、こちらも `drawnPoints` の末尾を見る。
 *
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 集約済みトレンドのうち値のある最新期間のラベルを返す
 * @graph-connects drawnPoints [calls] 描画対象の点に絞る
 */
export function latestPeriodLabel(points: TrendPoint[]): string | null {
  const drawn = drawnPoints(points);
  return drawn.length >= 1 ? drawn[drawn.length - 1].period : null;
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business KPIトレンドを、選択された期間（週次/月次/四半期/年度）の系列に集約する
 * @graph-connects none
 */
export function aggregateTrend(
  items: KpiTrendItem[],
  metric: MonthlyMetric,
  period: CsPeriod,
): TrendPoint[] {
  // 週の点を API からもらう指標は、日次から積み上げずにその点をそのまま出す。
  // 確定済みの週は指標ごとに違う（成功率はラグ、退会者数は完了週のみ）ので、
  // 値が無い週は落とす
  if (period === 'week' && WEEK_NATIVE_METRICS.has(metric)) {
    return items
      .filter((item) => item.granularity === 'week' && item[metric] !== null)
      .map((item) => ({ period: toWeekStartLabel(item.date), value: item[metric] }));
  }

  // 週次は日次データから積み上げる。日次を持つのは CS 系3指標だけで、
  // 他指標では空配列になりチャートが消える
  if (period === 'week') {
    const weekly = new Map<string, number[]>();
    for (const item of items) {
      if (item.granularity !== 'day') continue;
      const value = item[metric];
      if (value == null) continue;
      const label = toWeekLabel(item.date);
      const bucket = weekly.get(label) ?? [];
      bucket.push(value);
      weekly.set(label, bucket);
    }

    const useSumForWeek = isSumMetric(metric);
    return [...weekly.entries()].map(([label, values]) => {
      const total = values.reduce((acc, v) => acc + v, 0);
      return { period: label, value: useSumForWeek ? total : total / values.length };
    });
  }

  // 月次以上はどの指標も月の点を使う。成功率2本の月の点は API がコホート人数で
  // 加重した値で、週の単純平均とは一致しない（週ごとに母数が 500〜1,000 と差があるため）
  const source = items.filter((item) => item.granularity === 'month');

  if (period === 'month') {
    return source.map((item) => ({
      period: toMonthLabel(item.date),
      value: item[metric],
    }));
  }

  const buckets = new Map<string, number[]>();
  for (const item of source) {
    const value = item[metric];
    if (value == null) continue;
    const label = bucketLabel(period, item.date);
    const bucket = buckets.get(label) ?? [];
    bucket.push(value);
    buckets.set(label, bucket);
  }

  const useSum = isSumMetric(metric);
  return [...buckets.entries()].map(([label, values]) => {
    const total = values.reduce((acc, v) => acc + v, 0);
    return {
      period: label,
      value: useSum ? total : total / values.length,
    };
  });
}

/** @graph-connects none */
const AXIS_LABEL: Record<CsPeriod, string> = {
  week: '期間（週次・月曜起点）',
  month: '期間（月次）',
  quarter: '期間（四半期）',
  year: '期間（年度・7月始まり）',
};

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 選択された期間に応じたX軸ラベル文言を返す
 * @graph-connects none
 */
export function periodAxisLabel(period: CsPeriod, metric?: MonthlyMetric): string {
  // 区切りは CS 系と同じ月曜起点だが、こちらは「いつ登録した人か」の軸で、
  // CS 系の「いつ起きた出来事か」とは意味が違う。登録週だと明示する
  if (period === 'week' && metric !== undefined && COHORT_WEEK_METRICS.has(metric)) {
    return '期間（登録週・月曜起点）';
  }
  return AXIS_LABEL[period];
}

/**
 * Y軸ラベルに添える集約方法。月次は素の値なので null を返す。
 * 週次・四半期・年度は「合計」と「平均」で値の意味が変わるため、軸ラベル上で明示する。
 *
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 選択期間と指標に応じた集約方法ラベル（四半期合計 / 年度平均 等）を返す
 * @graph-connects none
 */
export function aggregationLabel(metric: MonthlyMetric, period: CsPeriod): string | null {
  if (period === 'month') return null;
  // 週次を API からそのままもらう「率」の指標は**畳んでいない**（成功率2本はコホートの値、
  // 待ち時間SLO2本と解決率系2本は API がその週の分子÷分母で出した率）。「週次平均」と
  // 出すと誤り。退会者数だけは API 側で日次を合計した値なので「週次合計」で正しい
  if (period === 'week' && WEEK_NATIVE_METRICS.has(metric) && !isSumMetric(metric)) return null;
  const scope = period === 'week' ? '週次' : period === 'quarter' ? '四半期' : '年度';
  return isSumMetric(metric) ? `${scope}合計` : `${scope}平均`;
}
